import * as THREE from 'three';
import { WORLD, LIGHTING, SHADOW } from '../config';
import { TerrainChunks } from './terrainChunks';
import { sharedEnvironment } from './envlight';
import { cloudFieldGLSL, cloudUniforms } from './clouds';

// Sky dome uniforms, driven live by the day-night cycle (engine/daynight.ts). Colours are LINEAR (the
// dome renders into the graded/linear target). uTime also drives the drifting clouds.
export interface SkyUniforms {
  uTime: { value: number };
  uZenith: { value: THREE.Color };
  uHorizon: { value: THREE.Color };
  uSunDir: { value: THREE.Vector3 };
  uSunColor: { value: THREE.Color };
  uCloud: { value: THREE.Color };
}

export interface World3D {
  scene: THREE.Scene;
  sun: THREE.DirectionalLight;
  hemi: THREE.HemisphereLight;
  terrain: TerrainChunks;
  sky: SkyUniforms;
  dome: THREE.Mesh; // sky dome -- main.ts re-centers it on the CAMERA every frame (identical sky in FP + 3P)
}

const SUN_DIR = new THREE.Vector3(LIGHTING.sunDirX, LIGHTING.sunDirY, LIGHTING.sunDirZ).normalize();

// Ground, lighting, and a shadow-casting sun. The sun is a single player-following shadow box
// (translation only, so the shadow direction stays constant). A RoomEnvironment PMREM gives
// PBR surfaces subtle ambient so they are not dead in shade.
export function buildWorld(renderer: THREE.WebGLRenderer): World3D {
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(WORLD.skyColor);
  scene.fog = new THREE.Fog(WORLD.skyColor, 260, 900); // pulled in from 920 so the outer mesh row (edge ~960) is fully fogged, no under-fogged band that pops at the horizon

  scene.environment = sharedEnvironment(renderer); // one PMREM env everywhere (thumbs + LOD tool share the helper)
  scene.environmentIntensity = LIGHTING.envIntensity;

  const sun = new THREE.DirectionalLight(LIGHTING.sunColor, LIGHTING.sunIntensity);
  sun.position.copy(SUN_DIR).multiplyScalar(LIGHTING.sunDistance);
  sun.castShadow = true;
  sun.shadow.mapSize.set(SHADOW.mapSize, SHADOW.mapSize);
  sun.shadow.bias = SHADOW.bias;
  sun.shadow.normalBias = SHADOW.normalBias;
  const sc = sun.shadow.camera;
  sc.near = SHADOW.near;
  sc.far = SHADOW.far;
  sc.left = -SHADOW.minHalf;
  sc.right = SHADOW.minHalf;
  sc.top = SHADOW.minHalf;
  sc.bottom = -SHADOW.minHalf;
  sc.updateProjectionMatrix();
  scene.add(sun, sun.target);
  const hemi = new THREE.HemisphereLight(LIGHTING.hemiSky, LIGHTING.hemiGround, LIGHTING.hemiIntensity);
  scene.add(hemi);

  // Sky colour uniforms (the day-night cycle overwrites these every frame). Linear-space colours.
  const sky: SkyUniforms = {
    uTime: { value: 0 },
    uZenith: { value: new THREE.Color(0x3f78d8).convertSRGBToLinear() },
    uHorizon: { value: new THREE.Color(0xbcd4ec).convertSRGBToLinear() },
    uSunDir: { value: SUN_DIR.clone() },
    uSunColor: { value: new THREE.Color(0xfff2da).convertSRGBToLinear() },
    uCloud: { value: new THREE.Color(0xffffff).convertSRGBToLinear() },
  };

  // Streamed terrain: a grid of chunk meshes that follows the camera (engine/terrainChunks.ts),
  // sharing the slope/height splat material. Replaces the old single ground plane so the world can
  // be kilometers across without one giant mesh.
  const terrain = new TerrainChunks(renderer, sky.uTime);
  scene.add(terrain.group);

  // Sea: the animated ocean surface (engine/water.ts) is added by main.ts so it can share the global
  // time uniform and follow the camera focus. (The old flat plane lived here.)

  // Sky dome: a big inside-out sphere with a (live) sky gradient, a sun disc + halo, and slow
  // procedural clouds in the sky band. CAMERA-CENTERED (main.ts re-positions it on the live camera
  // every frame): the old static-at-origin dome only had radius 800 while the island roams past
  // 1500m, so a zoomed-out third-person camera (or a player far from spawn) LEFT the sphere -- an
  // inside-out dome seen from outside is invisible, and the sky collapsed to the flat fog colour.
  // Centered on the eye, every camera mode sees the identical sky by construction. Fog off.
  const dome = new THREE.Mesh(new THREE.SphereGeometry(800, 32, 16), makeSkyMaterial(sky));
  dome.name = 'sky';
  dome.frustumCulled = false; // always surrounds the eye; never let a stale bounds cull it
  scene.add(dome);

  return { scene, sun, hemi, terrain, sky, dome };
}

// Procedural sky+clouds shader for the dome: a (live) horizon-to-zenith gradient, a sun disc + halo
// toward uSunDir, and drifting fbm clouds in the sky band. All colours come from uniforms the
// day-night cycle updates, so the sky shifts dawn -> noon -> dusk -> night.
function makeSkyMaterial(sky: SkyUniforms): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    side: THREE.BackSide,
    depthWrite: false,
    fog: false,
    uniforms: { ...(sky as unknown as Record<string, THREE.IUniform>), uCloudCover: cloudUniforms.uCloudCover },
    vertexShader: `
      varying vec3 vDir;
      void main(){ vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `
      varying vec3 vDir; uniform float uTime; uniform float uCloudCover;
      uniform vec3 uZenith, uHorizon, uSunColor, uCloud, uSunDir;
      float h(vec2 p){ p=fract(p*vec2(127.1,311.7)); p+=dot(p,p+34.5); return fract(p.x*p.y*95.43); }
      ${cloudFieldGLSL}
      void main(){
        vec3 vd = normalize(vDir);
        float y = clamp(vd.y, 0.0, 1.0);
        // Spread the gradient so the bright horizon colour is not pinned into a thin strip at y=0 (that
        // pinned strip + ACES clip is what read as a hard bright line where land meets sky).
        float g = pow(smoothstep(0.0, 1.0, y), 0.8);
        vec3 col = mix(uHorizon, uZenith, g);
        // Sun: a sharp disc plus a soft halo toward the sun direction.
        float sd = max(dot(vd, normalize(uSunDir)), 0.0);
        col += uSunColor * pow(sd, 350.0) * 2.2;
        col += uSunColor * pow(sd, 6.0) * 0.18 * smoothstep(0.0, 0.08, vd.y); // fade the low-sun halo off the horizon line
        // NIGHT SKY: stars fade in as the sun sinks -- hash-cell points with a slow twinkle, kept off
        // the horizon haze -- plus a moon disc + halo riding roughly opposite the sun. Drawn BEFORE
        // the cloud band so clouds occlude both.
        float night = 1.0 - smoothstep(-0.14, 0.02, uSunDir.y);
        if (night > 0.003) {
          vec2 sp = vd.xz / (1.0 + vd.y) * 90.0;
          vec2 cell = floor(sp); vec2 fr = fract(sp) - 0.5;
          float sc = h(cell);
          float star = smoothstep(0.965, 0.995, sc) * max(0.0, 1.0 - dot(fr, fr) * 9.0);
          star *= 0.6 + 0.4 * sin(uTime * (1.5 + sc * 4.0) + sc * 40.0);   // twinkle
          col += vec3(0.85, 0.9, 1.0) * star * night * smoothstep(0.06, 0.25, vd.y);
          vec3 mdir = normalize(vec3(-uSunDir.x, max(0.3, -uSunDir.y), -uSunDir.z));
          float md = max(dot(vd, mdir), 0.0);
          col += vec3(0.82, 0.88, 1.0) * (pow(md, 1400.0) * 1.5 + pow(md, 22.0) * 0.10) * night;
        }
        // HORIZON CONTINUATION: the SAME oxCloud field as the overhead sheet, projected onto the same
        // 240m virtual plane. Picks up exactly where the sheet's graze-fade hands off (view elevation
        // ~0.10-0.20), collapses to the field's smooth average at range exactly like the sheet's own
        // distance LOD, and melts into haze at the horizon line. Far clouds ARE the end of the sheet,
        // and both react to the weather's uCloudCover together.
        float ct = 240.0 / max(vd.y, 0.03);
        vec2 cwxz = cameraPosition.xz + vd.xz * ct;
        float cdd = mix(oxCloud(cwxz, uTime) + uCloudCover, 0.5 + uCloudCover, smoothstep(300.0, 1100.0, ct));
        float ccov = smoothstep(0.40, 0.62, cdd);
        ccov *= (1.0 - smoothstep(0.10, 0.20, vd.y)) * smoothstep(0.008, 0.05, vd.y);
        float cday = clamp(uSunDir.y * 3.0 + 0.25, 0.0, 1.0);
        vec3 ccol = mix(uCloud * 0.7, uCloud, smoothstep(0.5, 0.9, cdd)) * mix(0.16, 1.0, cday);
        col = mix(col, ccol, ccov * 0.9);
        gl_FragColor = vec4(col, 1.0);
      }`,
  });
}

// Move the sun + its shadow box to follow the focus (sun comes FROM `dir`), and resize the box to the
// current zoom. `dir` is the live day-night sun direction (normalized).
export function updateSunShadow(sun: THREE.DirectionalLight, focus: THREE.Vector3, viewHalf: number, dir: THREE.Vector3): void {
  sun.target.position.copy(focus);
  sun.position.copy(focus).addScaledVector(dir, LIGHTING.sunDistance);
  const half = Math.max(SHADOW.minHalf, viewHalf * SHADOW.frustumScale + SHADOW.frustumPad);
  const sc = sun.shadow.camera;
  if (sc.right !== half) {
    sc.left = -half;
    sc.right = half;
    sc.top = half;
    sc.bottom = -half;
    sc.updateProjectionMatrix();
  }
}
